// Company → Documents → Leases (N-110). Owner, 2026-09-26: "pull any lease
// info you can into the Lease table as well as any docs related to current
// locations that are lease docs … have them reflected in the Leases Table
// and document area for files."
//
// OWNER/DIRECTOR only, both reads and writes (unlike Licenses, which a
// manager also reads) — the pane itself is hidden from everyone else on the
// Company page (src/pages/Company.tsx), and canWriteLeases below is the
// same gate enforced again here, in case this ever renders without that.
//
// One table, every clinic, grouped by clinic in LOCATION_ORDER — unlike
// CompanyLicenses (one clinic at a time, picked by the page's own clinic
// tabs). Documents are a count chip that expands an inline list under the
// row, oldest first, each with its own Edit/Remove — the same closed-by-
// default expander CompanyLicenses's "Old Licenses" panel uses.
import type { JSX } from 'preact';
import { webLink } from '../lib/webLink';
import { useEffect, useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { FavoriteStar } from './FavoriteStar';
import { useWrite } from './MoneyForms';
import { useResource } from '../lib/store';
import { session } from '../lib/session';
import { clinicToday } from '../lib/clinicDay';
import { LOCATION_ORDER, LOCATION_NAMES } from '../lib/locations';
import { isoToMd } from '../lib/serviceCompliance';
import { extractDriveFileId } from '../lib/licenses';
import {
  LEASES_KEY, fetchLeases, reloadLeases, leaseGroups,
  addLease, updateLease, removeLease,
  addLeaseDocument, addLeaseDocumentForLocation, updateLeaseDocument, removeLeaseDocument,
  LEASE_STATUSES, LEASE_STATUS_LABEL,
  LEASE_DOCUMENT_KINDS, LEASE_DOCUMENT_KIND_LABEL, latestRenewal,
  LEASE_DOCUMENT_DATE_BASES, LEASE_DOCUMENT_DATE_BASIS_LABEL,
  leaseMonthsLeft, LEASE_ATTENTION_MONTHS, LEASE_WARN_MONTHS,
  type LeaseRow, type LeaseDocumentRow, type LeasesData, type LeaseGroup, type LeaseDocumentWrite,
} from '../lib/leases';

const EM_DASH = '—';

function canWriteLeases(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

/** The group's own row key — an existing lease keys on its id; a
 * placeholder (no lease yet) keys on the clinic, since there can only ever
 * be one such placeholder per clinic at a time (lib/leases.ts's leaseGroups). */
/** A clinic's lease page is a real address — `#leases-MELM` — so a refresh
 * and the Back button land on it (owner, 2026-09-26: "click on a clinic
 * location name … open a lease page for that particular location … kind of
 * how we have a people profile"). */
function profileClinicFromHash(): string | null {
  const m = /^#leases-([A-Z]{4})$/.exec(window.location.hash || '');
  return m ? m[1] : null;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function groupKey(group: LeaseGroup): string {
  return group.lease ? `lease-${group.lease.id}` : `empty-${group.clinic}`;
}

type LeaseFormState = {
  id: number;
  locationCode: string;
  premises: string;
  shoppingCenter: string;
  landlord: string;
  landlordContact: string;
  managementCompany: string;
  leaseDate: string;
  commencementDate: string;
  expirationDate: string;
  termNote: string;
  baseRentMonthly: string;
  rentNote: string;
  optionsNote: string;
  status: LeaseRow['status'];
  nextActionDate: string;
  nextAction: string;
  notes: string;
  source: string;
};

// Every editable text/date/amount column the server accepts, in one list, so
// the form, the blank state and the save body cannot drift apart again.
type LeaseTextKey = Exclude<keyof LeaseFormState, 'id' | 'locationCode' | 'status'>;

function blankLeaseForm(locationCode: string): LeaseFormState {
  return {
    id: 0,
    locationCode,
    premises: '',
    shoppingCenter: '',
    landlord: '',
    landlordContact: '',
    managementCompany: '',
    leaseDate: '',
    commencementDate: '',
    expirationDate: '',
    termNote: '',
    baseRentMonthly: '',
    rentNote: '',
    optionsNote: '',
    status: 'ACTIVE',
    nextActionDate: '',
    nextAction: '',
    notes: '',
    source: '',
  };
}

function leaseFormFrom(row: LeaseRow): LeaseFormState {
  return {
    id: row.id,
    locationCode: row.locationCode,
    premises: row.premises ?? '',
    shoppingCenter: row.shoppingCenter ?? '',
    landlord: row.landlord ?? '',
    landlordContact: row.landlordContact ?? '',
    managementCompany: row.managementCompany ?? '',
    leaseDate: row.leaseDate ?? '',
    commencementDate: row.commencementDate ?? '',
    expirationDate: row.expirationDate ?? '',
    termNote: row.termNote ?? '',
    baseRentMonthly: row.baseRentMonthly != null ? String(row.baseRentMonthly) : '',
    rentNote: row.rentNote ?? '',
    optionsNote: row.optionsNote ?? '',
    status: row.status,
    nextActionDate: row.nextActionDate ?? '',
    nextAction: row.nextAction ?? '',
    notes: row.notes ?? '',
    source: row.source ?? '',
  };
}

type DocFormState = {
  id: number;
  kind: string;
  title: string;
  docDate: string;
  dateBasis: string;
  linkInput: string;
};

function blankDocForm(): DocFormState {
  return { id: 0, kind: '', title: '', docDate: '', dateBasis: '', linkInput: '' };
}

function docFormFrom(row: LeaseDocumentRow): DocFormState {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    docDate: row.docDate ?? '',
    dateBasis: row.dateBasis ?? '',
    linkInput: row.url ?? row.driveUrl ?? '',
  };
}

export function CompanyLeases({ pinKey }: { pinKey?: string }): JSX.Element {
  const data = useResource<LeasesData>(LEASES_KEY, fetchLeases);
  const save = useWrite();
  const canWrite = canWriteLeases();
  const today = clinicToday();

  const [leaseForm, setLeaseForm] = useState<LeaseFormState | null>(null);
  const [confirmingLeaseId, setConfirmingLeaseId] = useState<number | null>(null);
  const [openDocs, setOpenDocs] = useState<Record<string, boolean>>({});
  const [docPanel, setDocPanel] = useState<{ key: string; form: DocFormState } | null>(null);
  const [profileClinic, setProfileClinic] = useState<string | null>(() => profileClinicFromHash());
  useEffect(() => {
    const onHash = (): void => setProfileClinic(profileClinicFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (data.status === 'loading') return <EmptyState label="Loading leases…" />;
  if (data.status === 'fault') return <FaultState name="leases_load_failed" detail={data.error} />;

  const groups = leaseGroups(data.data.leases ?? [], data.data.documents ?? []);

  function openLeaseForm(form: LeaseFormState): void {
    setDocPanel(null);
    setConfirmingLeaseId(null);
    setLeaseForm(form);
  }

  function openDocForm(key: string, form: DocFormState): void {
    setLeaseForm(null);
    setOpenDocs((prev) => ({ ...prev, [key]: true }));
    setDocPanel({ key, form });
  }

  function openProfile(clinic: string | null): void {
    setProfileClinic(clinic);
    // The hash is the address; Company.tsx's own hash listener keeps the
    // Management tab and the Leases disk selected for it.
    window.location.hash = clinic ? `leases-${clinic}` : 'leases';
  }

  async function submitLease(): Promise<void> {
    if (!leaseForm) return;
    await save.run(async () => {
      const body = {
        locationCode: leaseForm.locationCode,
        premises: leaseForm.premises.trim(),
        shoppingCenter: leaseForm.shoppingCenter.trim(),
        landlord: leaseForm.landlord.trim(),
        landlordContact: leaseForm.landlordContact.trim(),
        managementCompany: leaseForm.managementCompany.trim(),
        leaseDate: leaseForm.leaseDate,
        commencementDate: leaseForm.commencementDate,
        expirationDate: leaseForm.expirationDate,
        termNote: leaseForm.termNote.trim(),
        baseRentMonthly: leaseForm.baseRentMonthly.trim().replace(/^\$/, '').replace(/,/g, ''),
        rentNote: leaseForm.rentNote.trim(),
        optionsNote: leaseForm.optionsNote.trim(),
        status: leaseForm.status,
        nextActionDate: leaseForm.nextActionDate,
        nextAction: leaseForm.nextAction.trim(),
        notes: leaseForm.notes.trim(),
        source: leaseForm.source.trim(),
      };
      if (leaseForm.id) await updateLease(leaseForm.id, body);
      else await addLease(body);
      setLeaseForm(null);
      await reloadLeases();
    });
  }

  async function removeLeaseConfirmed(id: number): Promise<void> {
    await save.run(async () => {
      await removeLease(id);
      setConfirmingLeaseId(null);
      if (leaseForm?.id === id) setLeaseForm(null);
      await reloadLeases();
    });
  }

  async function submitDoc(group: LeaseGroup): Promise<void> {
    if (!docPanel) return;
    await save.run(async () => {
      const { form } = docPanel;
      const pasted = form.linkInput.trim();
      const driveFileId = extractDriveFileId(pasted);
      const body: LeaseDocumentWrite = {
        kind: form.kind as LeaseDocumentWrite['kind'],
        title: form.title.trim(),
        docDate: form.docDate,
        dateBasis: form.dateBasis as LeaseDocumentWrite['dateBasis'],
        driveFileId: driveFileId ?? '',
        // A recognisable Drive file link resolves to driveFileId above; any
        // other link (OneDrive, a Monday board item, anything else the
        // leases actually live in) is kept verbatim in `url` instead of
        // being discarded for not being a Drive link.
        url: driveFileId ? '' : pasted,
        sourcePath: '',
        sha256: '',
        note: '',
      };
      if (form.id) await updateLeaseDocument(form.id, body);
      else if (group.lease) await addLeaseDocument(group.lease.id, body);
      else await addLeaseDocumentForLocation(group.clinic, body);
      setDocPanel(null);
      await reloadLeases();
    });
  }

  async function removeDoc(id: number): Promise<void> {
    await save.run(async () => {
      await removeLeaseDocument(id);
      await reloadLeases();
    });
  }

  /** The filed paperwork for one group, oldest first, with Edit / Remove and
   * the Add document form — the same panel under a table row and on a
   * clinic's lease page. */
  function renderDocuments(group: LeaseGroup, key: string): JSX.Element {
    return (
      <>
                        {group.documents.length === 0 ? (
                          <p class="empty">No documents filed yet.</p>
                        ) : (
                          <ul class="lease-document-list">
                            {group.documents.map((doc) => {
                              const href = webLink(doc.driveUrl ?? doc.url);
                              return (
                                <li key={doc.id} data-testid={`lease-document-${doc.id}`}>
                                  <span>{isoToMd(doc.docDate)}</span>
                                  <span>{LEASE_DOCUMENT_KIND_LABEL[doc.kind]}</span>
                                  {href ? (
                                    <a class="doc-link" href={href} target="_blank" rel="noreferrer">{doc.title}</a>
                                  ) : (
                                    <span>{doc.title}</span>
                                  )}
                                  <span class="lease-doc-note">{doc.note || EM_DASH}</span>
                                  {canWrite ? (
                                    <span class="lease-doc-actions">
                                      <button
                                        type="button"
                                        class="btn"
                                        disabled={save.busy}
                                        onClick={() => openDocForm(key, docFormFrom(doc))}
                                      >
                                        Edit
                                      </button>
                                      <button type="button" class="btn" disabled={save.busy} onClick={() => void removeDoc(doc.id)}>
                                        Remove
                                      </button>
                                    </span>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {canWrite ? (
                          docPanel?.key === key ? (
                            <form
                              class="service-point-form"
                              data-testid="lease-document-form"
                              onSubmit={(e) => { e.preventDefault(); void submitDoc(group); }}
                            >
                              <label>
                                Kind
                                <select
                                  aria-label="Document kind"
                                  data-testid="lease-document-field-kind"
                                  value={docPanel.form.kind}
                                  onChange={(e) => setDocPanel({ key, form: { ...docPanel.form, kind: (e.target as HTMLSelectElement).value } })}
                                >
                                  <option value="">— choose —</option>
                                  {LEASE_DOCUMENT_KINDS.map((k) => (
                                    <option key={k} value={k}>{LEASE_DOCUMENT_KIND_LABEL[k]}</option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                Title
                                <input
                                  data-testid="lease-document-field-title"
                                  value={docPanel.form.title}
                                  onInput={(e) => setDocPanel({ key, form: { ...docPanel.form, title: (e.target as HTMLInputElement).value } })}
                                />
                              </label>
                              <label>
                                Document date
                                <input
                                  type="date"
                                  data-testid="lease-document-field-date"
                                  value={docPanel.form.docDate}
                                  onInput={(e) => setDocPanel({ key, form: { ...docPanel.form, docDate: (e.target as HTMLInputElement).value } })}
                                />
                              </label>
                              <label>
                                Date basis
                                <select
                                  aria-label="Date basis"
                                  data-testid="lease-document-field-basis"
                                  value={docPanel.form.dateBasis}
                                  onChange={(e) => setDocPanel({ key, form: { ...docPanel.form, dateBasis: (e.target as HTMLSelectElement).value } })}
                                >
                                  <option value="">— none —</option>
                                  {LEASE_DOCUMENT_DATE_BASES.map((b) => (
                                    <option key={b} value={b}>{LEASE_DOCUMENT_DATE_BASIS_LABEL[b]}</option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                Drive link
                                <input
                                  data-testid="lease-document-field-link"
                                  placeholder="https://drive.google.com/file/d/… (or any other link the file lives at)"
                                  value={docPanel.form.linkInput}
                                  onInput={(e) => setDocPanel({ key, form: { ...docPanel.form, linkInput: (e.target as HTMLInputElement).value } })}
                                />
                              </label>
                              <div class="service-point-actions">
                                <button
                                  type="submit"
                                  class="btn primary"
                                  disabled={save.busy || !docPanel.form.kind || !docPanel.form.title.trim()}
                                >
                                  {docPanel.form.id ? 'Save' : 'Add'}
                                </button>
                                <button type="button" class="btn" disabled={save.busy} onClick={() => setDocPanel(null)}>
                                  Cancel
                                </button>
                              </div>
                            </form>
                          ) : (
                            <button
                              type="button"
                              class="btn"
                              data-testid={`lease-add-document-${key}`}
                              onClick={() => openDocForm(key, blankDocForm())}
                            >
                              Add document
                            </button>
                          )
                        ) : null}
      </>
    );
  }

  return (
    <div class="card" data-testid="company-leases">
      <div class="section-head">
        <div class="section-head-row">
          <h2>Leases</h2>
          <div class="section-head-actions">
            {canWrite ? (
              <button
                type="button"
                class="btn primary"
                data-testid="add-lease"
                onClick={() => openLeaseForm(blankLeaseForm(LOCATION_ORDER[0]))}
              >
                Add lease
              </button>
            ) : null}
            {pinKey ? <FavoriteStar itemKey={pinKey} /> : null}
          </div>
        </div>
        <p>Every clinic&apos;s lease, and the paperwork filed against it, oldest to newest.</p>
        {save.error ? <p class="bad" role="alert">{save.error}</p> : null}
      </div>

      {leaseForm ? (
        <form
          class="service-point-form"
          data-testid="lease-form"
          onSubmit={(e) => { e.preventDefault(); void submitLease(); }}
        >
          <label>
            Clinic
            <select
              aria-label="Clinic"
              data-testid="lease-field-clinic"
              value={leaseForm.locationCode}
              disabled={Boolean(leaseForm.id)}
              onChange={(e) => setLeaseForm({ ...leaseForm, locationCode: (e.target as HTMLSelectElement).value })}
            >
              {LOCATION_ORDER.map((code) => (
                <option key={code} value={code}>{LOCATION_NAMES[code]}</option>
              ))}
            </select>
          </label>
          <fieldset class="lease-form-group">
            <legend>Parties</legend>
          <label>
            Premises
            <input
              data-testid="lease-field-premises"
              value={leaseForm.premises}
              onInput={(e) => setLeaseForm({ ...leaseForm, premises: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Shopping center
            <input
              data-testid="lease-field-shopping-center"
              value={leaseForm.shoppingCenter}
              onInput={(e) => setLeaseForm({ ...leaseForm, shoppingCenter: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Landlord
            <input
              data-testid="lease-field-landlord"
              value={leaseForm.landlord}
              onInput={(e) => setLeaseForm({ ...leaseForm, landlord: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Landlord contact
            <input
              data-testid="lease-field-contact"
              value={leaseForm.landlordContact}
              onInput={(e) => setLeaseForm({ ...leaseForm, landlordContact: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Management company
            <input
              data-testid="lease-field-management-company"
              value={leaseForm.managementCompany}
              onInput={(e) => setLeaseForm({ ...leaseForm, managementCompany: (e.target as HTMLInputElement).value })}
            />
          </label>
          </fieldset>
          <fieldset class="lease-form-group">
            <legend>Term</legend>
          <label>
            Lease date (MM/DD/YYYY)
            <input
              type="date"
              data-testid="lease-field-lease-date"
              value={leaseForm.leaseDate}
              onInput={(e) => setLeaseForm({ ...leaseForm, leaseDate: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Commencement date (MM/DD/YYYY)
            <input
              type="date"
              data-testid="lease-field-commencement-date"
              value={leaseForm.commencementDate}
              onInput={(e) => setLeaseForm({ ...leaseForm, commencementDate: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Current term ends (MM/DD/YYYY)
            <input
              type="date"
              data-testid="lease-field-term-ends"
              value={leaseForm.expirationDate}
              onInput={(e) => setLeaseForm({ ...leaseForm, expirationDate: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Term note
            <input
              data-testid="lease-field-term-note"
              value={leaseForm.termNote}
              onInput={(e) => setLeaseForm({ ...leaseForm, termNote: (e.target as HTMLInputElement).value })}
            />
          </label>
          </fieldset>
          <fieldset class="lease-form-group">
            <legend>Rent</legend>
          <label>
            Base rent per month ($)
            <input
               inputMode="decimal"
              data-testid="lease-field-base-rent"
              value={leaseForm.baseRentMonthly}
              onInput={(e) => setLeaseForm({ ...leaseForm, baseRentMonthly: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Rent note
            <input
              data-testid="lease-field-rent-note"
              value={leaseForm.rentNote}
              onInput={(e) => setLeaseForm({ ...leaseForm, rentNote: (e.target as HTMLInputElement).value })}
            />
          </label>
          </fieldset>
          <fieldset class="lease-form-group">
            <legend>Status and next step</legend>
          <label>
            Status
            <select
              aria-label="Status"
              data-testid="lease-field-status"
              value={leaseForm.status}
              onChange={(e) => setLeaseForm({ ...leaseForm, status: (e.target as HTMLSelectElement).value as LeaseRow['status'] })}
            >
              {LEASE_STATUSES.map((s) => (
                <option key={s} value={s}>{LEASE_STATUS_LABEL[s]}</option>
              ))}
            </select>
          </label>
          <label>
            Next action
            <input
              data-testid="lease-field-next-action"
              value={leaseForm.nextAction}
              onInput={(e) => setLeaseForm({ ...leaseForm, nextAction: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Next action date (MM/DD/YYYY)
            <input
              type="date"
              data-testid="lease-field-next-action-date"
              value={leaseForm.nextActionDate}
              onInput={(e) => setLeaseForm({ ...leaseForm, nextActionDate: (e.target as HTMLInputElement).value })}
            />
          </label>
          </fieldset>
          <fieldset class="lease-form-group">
            <legend>Options and notes</legend>
          <label>
            Options (renewal, extension)
            <textarea
              rows={2}
              data-testid="lease-field-options-note"
              value={leaseForm.optionsNote}
              onInput={(e) => setLeaseForm({ ...leaseForm, optionsNote: (e.target as HTMLTextAreaElement).value })}
            />
          </label>
          <label>
            Notes
            <textarea
              rows={2}
              data-testid="lease-field-notes"
              value={leaseForm.notes}
              onInput={(e) => setLeaseForm({ ...leaseForm, notes: (e.target as HTMLTextAreaElement).value })}
            />
          </label>
          <label>
            Source
            <input
              data-testid="lease-field-source"
              value={leaseForm.source}
              onInput={(e) => setLeaseForm({ ...leaseForm, source: (e.target as HTMLInputElement).value })}
            />
          </label>
          </fieldset>
          <div class="service-point-actions">
            <button type="submit" class="btn primary" disabled={save.busy}>
              {leaseForm.id ? 'Save' : 'Add'}
            </button>
            <button type="button" class="btn" disabled={save.busy} onClick={() => setLeaseForm(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {profileClinic ? (
        <div data-testid="lease-profile">
          <div class="candidate-page-bar">
            <button type="button" class="btn" data-testid="lease-profile-back" onClick={() => openProfile(null)}>Back to all leases</button>
          </div>
          {groups.filter((g) => g.clinic === profileClinic).map((group) => {
            const key = groupKey(group);
            const { lease } = group;
            const renewal = latestRenewal(group.documents);
            const monthsLeft = lease?.expirationDate ? leaseMonthsLeft(lease.expirationDate, today) : null;
            const wash = monthsLeft !== null && monthsLeft >= 0 && monthsLeft <= LEASE_WARN_MONTHS ? 'license-expired'
              : monthsLeft !== null && monthsLeft >= 0 && monthsLeft <= LEASE_ATTENTION_MONTHS ? 'license-due' : undefined;
            const confirming = lease ? confirmingLeaseId === lease.id : false;
            return (
              <section class="lease-profile" key={key} data-testid={`lease-profile-${key}`}>
                <div class="section-head">
                  <div class="section-head-row">
                    <h3>{LOCATION_NAMES[group.clinic as keyof typeof LOCATION_NAMES] ?? group.clinic} lease</h3>
                    <div class="section-head-actions">
                      {lease ? <span class="badge">{LEASE_STATUS_LABEL[lease.status]}</span> : null}
                      {canWrite && lease ? (
                        confirming ? (
                          <>
                            <span>Remove this lease?</span>
                            <button type="button" class="btn" disabled={save.busy} onClick={() => void removeLeaseConfirmed(lease.id)}>Confirm</button>
                            <button type="button" class="btn" disabled={save.busy} onClick={() => setConfirmingLeaseId(null)}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button type="button" class="btn" disabled={save.busy} onClick={() => openLeaseForm(leaseFormFrom(lease))}>Edit</button>
                            <button type="button" class="btn" disabled={save.busy} onClick={() => setConfirmingLeaseId(lease.id)}>Remove</button>
                          </>
                        )
                      ) : null}
                      {canWrite && !lease ? (
                        <button type="button" class="btn" disabled={save.busy} onClick={() => openLeaseForm(blankLeaseForm(group.clinic))}>Add lease</button>
                      ) : null}
                    </div>
                  </div>
                </div>
                <table data-testid={`lease-facts-${key}`}>
                  <tbody>
                    <tr><th scope="row">Premises</th><td>{lease?.premises || EM_DASH}</td></tr>
                    <tr><th scope="row">Shopping center</th><td>{lease?.shoppingCenter || EM_DASH}</td></tr>
                    <tr><th scope="row">Landlord</th><td>{lease?.landlord || EM_DASH}</td></tr>
                    <tr><th scope="row">Contact</th><td>{lease?.landlordContact || EM_DASH}</td></tr>
                    <tr><th scope="row">Management company</th><td>{lease?.managementCompany || EM_DASH}</td></tr>
                    <tr><th scope="row">Lease date</th><td>{lease?.leaseDate ? isoToMd(lease.leaseDate) : EM_DASH}</td></tr>
                    <tr><th scope="row">Commencement</th><td>{lease?.commencementDate ? isoToMd(lease.commencementDate) : EM_DASH}</td></tr>
                    <tr><th scope="row">Renewed</th><td>{renewal ? `${isoToMd(renewal.docDate)} (${LEASE_DOCUMENT_KIND_LABEL[renewal.kind]}: ${renewal.title})` : EM_DASH}</td></tr>
                    <tr class={wash}>
                      <th scope="row">Current term ends</th>
                      <td>
                        {lease?.expirationDate ? isoToMd(lease.expirationDate) : EM_DASH}
                        {monthsLeft !== null && monthsLeft >= 0 && monthsLeft <= LEASE_ATTENTION_MONTHS
                          ? ` (in ${monthsLeft} month${monthsLeft === 1 ? '' : 's'})` : ''}
                      </td>
                    </tr>
                    <tr><th scope="row">Term</th><td>{lease?.termNote || EM_DASH}</td></tr>
                    <tr><th scope="row">Base rent</th><td>{lease?.baseRentMonthly != null ? `${USD.format(Number(lease.baseRentMonthly))} / month` : EM_DASH}</td></tr>
                    <tr><th scope="row">Rent</th><td>{lease?.rentNote || EM_DASH}</td></tr>
                    <tr><th scope="row">Options</th><td>{lease?.optionsNote || EM_DASH}</td></tr>
                    <tr><th scope="row">Next action</th><td>{lease?.nextAction || EM_DASH}{lease?.nextActionDate ? ` (${isoToMd(lease.nextActionDate)})` : ''}</td></tr>
                    <tr><th scope="row">Notes</th><td>{lease?.notes || EM_DASH}</td></tr>
                    <tr><th scope="row">Source</th><td>{lease?.source || EM_DASH}</td></tr>
                  </tbody>
                </table>
                <h3>{`Documents (${group.documents.length})`}</h3>
                {renderDocuments(group, key)}
              </section>
            );
          })}
        </div>
      ) : (
      <div class="table-wrap">
        <table class="data" data-testid="leases-table">
          <thead>
            <tr>
              <th>Clinic</th>
              <th>Premises</th>
              <th>Landlord</th>
              <th>Contact</th>
              <th>Lease date</th>
              <th>Renewed</th>
              <th>Current term ends</th>
              <th>Status</th>
              <th>Next action</th>
              <th>Documents</th>
              {canWrite ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((group) => {
              const key = groupKey(group);
              const renewal = latestRenewal(group.documents);
              const { lease } = group;
              // Owner, 2026-09-26: "i need alert 12 month in advance and 6
              // months in advance on all these lease expirations" — the same
              // hint the My Desk "Needs attention" panel shows, here on the
              // row itself. null when there is no expiration date to measure.
              const monthsLeft = lease?.expirationDate ? leaseMonthsLeft(lease.expirationDate, today) : null;
              const docsOpen = Boolean(openDocs[key]);
              const confirming = lease ? confirmingLeaseId === lease.id : false;
              // A plain array, not a Fragment: two independently-keyed <tr>
              // elements (the row and, when open, its documents panel)
              // flattened straight into <tbody>'s own row list, rather than
              // a shorthand <>...</> fragment — which cannot carry the key
              // each row still needs once documents panels start opening
              // and closing independently of one another.
              const rows: (JSX.Element | null)[] = [
                (
                  <tr
                    key={key}
                    data-testid={`lease-row-${key}`}
                    // The whole row takes the Licenses table's washes (owner,
                    // 2026-09-26: "it should also get highlighted"): red
                    // inside 6 months of the term's end, orange inside 12.
                    class={
                      monthsLeft !== null && monthsLeft >= 0 && monthsLeft <= LEASE_WARN_MONTHS ? 'license-expired'
                        : monthsLeft !== null && monthsLeft >= 0 && monthsLeft <= LEASE_ATTENTION_MONTHS ? 'license-due'
                          : undefined
                    }
                  >
                    <td>
                      <a
                        class="candidate-link"
                        href={`#leases-${group.clinic}`}
                        data-testid={`lease-open-${group.clinic}`}
                        onClick={(e: MouseEvent) => { e.preventDefault(); openProfile(group.clinic); }}
                      >
                        {LOCATION_NAMES[group.clinic as keyof typeof LOCATION_NAMES] ?? group.clinic}
                      </a>
                    </td>
                    <td>{lease?.premises || EM_DASH}</td>
                    <td>{lease?.landlord || EM_DASH}</td>
                    <td>{lease?.landlordContact || EM_DASH}</td>
                    <td>{lease?.leaseDate ? isoToMd(lease.leaseDate) : EM_DASH}</td>
                    <td data-testid={`lease-renewed-${key}`}>
                      {renewal ? <>{isoToMd(renewal.docDate)}<div class="muted">{LEASE_DOCUMENT_KIND_LABEL[renewal.kind]}</div></> : EM_DASH}
                    </td>
                    <td data-testid={`lease-term-ends-${key}`}>
                      {lease?.expirationDate ? (
                        <>
                          {isoToMd(lease.expirationDate)}
                          {monthsLeft !== null && monthsLeft >= 0 && monthsLeft <= LEASE_ATTENTION_MONTHS ? (
                            <div class={monthsLeft <= LEASE_WARN_MONTHS ? 'muted warn' : 'muted'}>
                              {`in ${monthsLeft} month${monthsLeft === 1 ? '' : 's'}`}
                            </div>
                          ) : null}
                        </>
                      ) : EM_DASH}
                    </td>
                    <td>{lease ? <span class="badge">{LEASE_STATUS_LABEL[lease.status]}</span> : EM_DASH}</td>
                    <td>
                      {lease?.nextAction || EM_DASH}
                      {lease?.nextActionDate ? <div>{isoToMd(lease.nextActionDate)}</div> : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        class="btn"
                        data-testid={`lease-documents-toggle-${key}`}
                        onClick={() => setOpenDocs((prev) => ({ ...prev, [key]: !prev[key] }))}
                      >
                        {`Documents (${group.documents.length})`}
                      </button>
                    </td>
                    {canWrite ? (
                      <td>
                        {lease ? (
                          confirming ? (
                            <>
                              <span>Remove this lease?</span>
                              <button type="button" class="btn" disabled={save.busy} onClick={() => void removeLeaseConfirmed(lease.id)}>
                                Confirm
                              </button>
                              <button type="button" class="btn" disabled={save.busy} onClick={() => setConfirmingLeaseId(null)}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button type="button" class="btn" disabled={save.busy} onClick={() => openLeaseForm(leaseFormFrom(lease))}>
                                Edit
                              </button>
                              <button
                                type="button"
                                class="btn"
                                data-testid={`lease-remove-${lease.id}`}
                                disabled={save.busy}
                                onClick={() => setConfirmingLeaseId(lease.id)}
                              >
                                Remove
                              </button>
                            </>
                          )
                        ) : (
                          <button type="button" class="btn" disabled={save.busy} onClick={() => openLeaseForm(blankLeaseForm(group.clinic))}>
                            Add lease
                          </button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ),
                docsOpen ? (
                  <tr key={`${key}-docs`} data-testid={`lease-documents-panel-${key}`}>
                    <td colSpan={canWrite ? 11 : 10}>
                      {renderDocuments(group, key)}
                    </td>
                  </tr>
                ) : null,
              ];
              return rows;
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
